import { FREE_TIP_ID } from '../data/examTipsData';
import faqData from '../assets/faq.json';
import { getPublicDialogueVideoFallback } from './publicDialogueVideoFallback';
import {
  buildExamOverviewPath,
  buildExamTipPath,
  buildExampleVideoPath,
  buildExampleVideosPath,
  buildMockExamsPath,
  buildPracticePath,
  buildPracticeSectionPath,
  buildTopicsDiscussionPath,
  buildTopicsVideoPath,
  normalizeLanguage,
} from '../utils/contentRoutes';

const BASE_URL = 'https://fideprep.ch';
/** Generated by `node scripts/generate-og-image.js`. */
export const OG_IMAGE_URL = `${BASE_URL}/og-image.png`;
export const OG_IMAGE_SQUARE_URL = `${BASE_URL}/og-image-square.jpg`;
const TOPIC_VIDEO_LIBRARY_ID = '613124';
const TOPIC_VIDEO_CDN_BASE = 'https://vz-1f9fdf9a-bd2.b-cdn.net';
const TOPIC_VIDEO_PLAYER_BASE = `https://iframe.mediadelivery.net/embed/${TOPIC_VIDEO_LIBRARY_ID}`;
const META_DESCRIPTION_MAX_LENGTH = 155;

export const capMetaDescription = (value) => {
  if (value.length <= META_DESCRIPTION_MAX_LENGTH) return value;

  const shortened = value
    .slice(0, META_DESCRIPTION_MAX_LENGTH - 1)
    .replace(/\s+\S*$/, '')
    .trimEnd();
  return `${shortened || value.slice(0, META_DESCRIPTION_MAX_LENGTH - 1).trimEnd()}…`;
};

const FAQ_MAIN_ENTITY = faqData
  .filter((faq) => typeof faq.answer === 'string' && faq.answer.length > 0)
  .map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  }));

const getLocalizedText = (value, lang = 'fr') => {
  if (typeof value === 'string') {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return '';
  }

  return value[lang] || value.fr || value.de || value.en || '';
};

const formatLevelList = (levels = []) => {
  const filteredLevels = Array.isArray(levels) ? levels.filter(Boolean) : [];
  if (filteredLevels.length === 0) {
    return 'A1 to B1';
  }
  if (filteredLevels.length === 1) {
    return filteredLevels[0];
  }
  if (filteredLevels.length === 2) {
    return `${filteredLevels[0]} and ${filteredLevels[1]}`;
  }

  return `${filteredLevels.slice(0, -1).join(', ')}, and ${filteredLevels[filteredLevels.length - 1]}`;
};

const formatTopicList = (topics = []) => {
  const filteredTopics = Array.isArray(topics) ? topics.filter(Boolean) : [];
  if (filteredTopics.length === 0) {
    return 'French daily-life situations';
  }
  if (filteredTopics.length === 1) {
    return filteredTopics[0];
  }
  if (filteredTopics.length === 2) {
    return `${filteredTopics[0]} and ${filteredTopics[1]}`;
  }

  return `${filteredTopics.slice(0, -1).join(', ')}, and ${filteredTopics[filteredTopics.length - 1]}`;
};

export const buildCanonicalUrl = (path = '/') => {
  try {
    const url = new URL(path, BASE_URL);
    // Keep trailing slash only for the homepage
    if (path !== '/' && url.pathname.endsWith('/')) {
      url.pathname = url.pathname.replace(/\/+$/, '');
    }
    return url.toString();
  } catch (error) {
    return `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  }
};

export const buildAlternates = ({ fr, de }) => {
  const list = [];
  if (fr) list.push({ hreflang: 'fr', href: buildCanonicalUrl(fr) });
  if (de) list.push({ hreflang: 'de', href: buildCanonicalUrl(de) });
  if (fr) list.push({ hreflang: 'x-default', href: buildCanonicalUrl(fr) });
  return list;
};

export const PAGE_META = {
  home: {
    path: '/',
    title: 'FIDE Test Preparation | French & German A1–B1 | FIDE Prep',
    description:
      'Free online fide test preparation for French and German: vocabulary, flashcards, sample papers, speaking practice and realistic mock oral exams.',
    openGraphImage: OG_IMAGE_URL,
    twitterCard: 'summary_large_image',
    alternates: [{ hreflang: 'x-default', href: 'https://fideprep.ch/' }],
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': 'https://fideprep.ch/#organization',
          name: 'FIDE Prep',
          url: 'https://fideprep.ch/',
          description: 'French and German FIDE test preparation for A1 to B1 candidates in Switzerland.',
          logo: 'https://fideprep.ch/logo.png',
          areaServed: {
            '@type': 'Country',
            name: 'Switzerland',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            email: 'info@fideprep.ch',
            telephone: '+41 21 553 62 18',
            contactType: 'customer support',
            areaServed: 'CH',
          },
        },
        {
          '@type': 'WebSite',
          '@id': 'https://fideprep.ch/#website',
          name: 'FIDE Prep',
          url: 'https://fideprep.ch/',
          description:
            'French and German FIDE test preparation with practice topics, mock exams, and study tools for A1 to B1 candidates in Switzerland.',
          inLanguage: ['fr', 'de'],
          publisher: { '@id': 'https://fideprep.ch/#organization' },
        },
        {
          '@type': 'WebPage',
          '@id': 'https://fideprep.ch/#webpage',
          url: 'https://fideprep.ch/',
          isPartOf: { '@id': 'https://fideprep.ch/#website' },
          mainEntity: { '@id': 'https://fideprep.ch/#course' },
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: OG_IMAGE_SQUARE_URL,
            width: 1254,
            height: 1254,
          },
        },
        {
          '@type': 'FAQPage',
          '@id': 'https://fideprep.ch/#faq',
          mainEntity: FAQ_MAIN_ENTITY,
        },
        {
          '@type': 'Course',
          '@id': 'https://fideprep.ch/#course',
          name: 'fide test preparation — French and German, A1 to B1',
          description:
            'Guided preparation for the Swiss fide language test: speaking practice for every exam task, vocabulary, flashcards, real exam example videos and timed mock oral exams. Full access to every topic, all mock exam papers and the exam tips section is CHF 49 for three months.',
          url: 'https://fideprep.ch/',
          image: [OG_IMAGE_SQUARE_URL, OG_IMAGE_URL],
          inLanguage: ['fr', 'de'],
          educationalLevel: 'CEFR A1, A2, B1',
          teaches: 'French and German for the Swiss fide language test',
          provider: { '@id': 'https://fideprep.ch/#organization' },
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'online',
            courseWorkload: 'PT1H',
          },
          // Most of the material is free to use without an account. The optional
          // Offer supplies accurate price context without representing the access
          // pass as a separate merchant Product.
          isAccessibleForFree: true,
          offers: {
            '@type': 'Offer',
            // Google requires Offer.category on a Course; most material is free to use
            // and the CHF 49 pass unlocks the rest, which is "Partially Free".
            category: 'Partially Free',
            price: '49',
            priceCurrency: 'CHF',
            url: 'https://fideprep.ch/pricing',
            availability: 'https://schema.org/InStock',
          },
        },
      ],
    },
  },
  mockExamFlow: {
    path: '/mock-exam/flow',
    title: 'Interactive FIDE Oral Test Practice with Sample Answers | FIDE Prep',
    description:
      'Take an interactive FIDE oral test practice with real-time tips, sample answers, and answer recording. Practice speaking and listening for the FIDE test A1, A2, and B1.',
    noindex: true,
  },
  mockExamReview: {
    path: '/mock-exam/review/:examId',
    title: 'Oral Test Review | Answers & Recordings | FIDE Prep',
    description:
      'Review your FIDE oral test results with a full summary, sample answers, and your recorded responses. Track your progress and improve your speaking and listening skills for the FIDE test.',
    noindex: true,
  },
  topicsFr: {
    path: '/fr/fide-test-topics',
    title: 'FIDE Test Topics (French) | A1-B1 Oral & Written Preparation',
    description:
      'Explore FIDE French test topics for A1–B1: everyday dialogues and discussions to practice speaking for the oral exam.',
    alternates: buildAlternates({ fr: '/fr/fide-test-topics', de: '/de/fide-test-topics' }),
  },
  topicsDe: {
    path: '/de/fide-test-topics',
    title: 'FIDE Test Topics (German) | A1-B1 Oral & Written Preparation',
    description:
      'Explore FIDE German test topics for A1–B1: everyday dialogues and discussions to practice speaking for the oral exam.',
    alternates: buildAlternates({ fr: '/fr/fide-test-topics', de: '/de/fide-test-topics' }),
  },
  discussionTopicsFr: {
    path: buildTopicsDiscussionPath('fr'),
    title: 'FIDE Discussion Topics (French) | B1 Oral Test Preparation',
    description:
      'Master FIDE French discussion topics. Practice expressing opinions on Swiss daily-life subjects to prepare for the B1 oral exam and speak with confidence.',
    alternates: buildAlternates({ fr: buildTopicsDiscussionPath('fr'), de: buildTopicsDiscussionPath('de') }),
  },
  discussionTopicsDe: {
    path: buildTopicsDiscussionPath('de'),
    title: 'FIDE Discussion Topics (German) | B1 Oral Test Preparation',
    description:
      'Master FIDE German discussion topics. Practice expressing opinions on Swiss daily-life subjects to prepare for the B1 oral exam and speak with confidence.',
    alternates: buildAlternates({ fr: buildTopicsDiscussionPath('fr'), de: buildTopicsDiscussionPath('de') }),
  },
  videos: {
    path: buildExampleVideosPath(),
    title: 'FIDE Test Example Videos (French) | A1-B1 Oral Test Samples',
    description:
      'Watch French FIDE test example videos with transcripts. Filter by topic or level, then practice listening for the oral exam.',
  },
  vocabularyFr: {
    path: '/fr/fide-vocabulary',
    title: 'FIDE Exam Vocabulary (French) | A1-B1 Preparation Lists',
    description:
      'French FIDE exam vocabulary for A1–B1: browse topic-based words and phrases, then build study lists for test preparation.',
    alternates: buildAlternates({ fr: '/fr/fide-vocabulary', de: '/de/fide-vocabulary' }),
  },
  vocabularyDe: {
    path: '/de/fide-vocabulary',
    title: 'FIDE Exam Vocabulary (German) | A1-B1 Preparation Lists',
    description:
      'German FIDE exam vocabulary for A1–B1: browse topic-based words and phrases, then build study lists for test preparation.',
    alternates: buildAlternates({ fr: '/fr/fide-vocabulary', de: '/de/fide-vocabulary' }),
  },
  practiceFr: {
    path: '/fr/fide-flashcards',
    title: 'FIDE French Flashcards | A1-B1 Vocabulary Practice',
    description:
      'Practice French FIDE vocabulary with free interactive flashcards. Study by topic, save lists and prepare for the FIDE test.',
    alternates: buildAlternates({ fr: '/fr/fide-flashcards', de: '/de/fide-flashcards' }),
  },
  practiceDe: {
    path: '/de/fide-flashcards',
    title: 'FIDE German Flashcards | A1-B1 Vocabulary Practice',
    description:
      'Practice German FIDE vocabulary with free interactive flashcards. Study by topic, save lists and prepare for the FIDE test.',
    alternates: buildAlternates({ fr: '/fr/fide-flashcards', de: '/de/fide-flashcards' }),
  },
  vocabularyToolFr: {
    path: '/fr/fide-practice/topics/all/vocab',
    title: 'FIDE Vocabulary Tool | FIDE Prep',
    description: 'Browse and study FIDE vocabulary by topic.',
    noindex: true,
  },
  vocabularyToolDe: {
    path: '/de/fide-practice/topics/all/vocab',
    title: 'FIDE Vocabulary Tool | FIDE Prep',
    description: 'Browse and study FIDE vocabulary by topic.',
    noindex: true,
  },
  flashcardsToolFr: {
    path: '/fr/fide-practice/topics/all/flashcards',
    title: 'FIDE Flashcard Tool | FIDE Prep',
    description: 'Study FIDE vocabulary with interactive flashcards.',
    noindex: true,
  },
  flashcardsToolDe: {
    path: '/de/fide-practice/topics/all/flashcards',
    title: 'FIDE Flashcard Tool | FIDE Prep',
    description: 'Study FIDE vocabulary with interactive flashcards.',
    noindex: true,
  },
  authLogin: {
    path: '/auth/login',
    title: 'Log In | FIDE Prep',
    description: 'Log in to your FIDE Prep account to access your personalized study plans and vocabulary lists.',
    noindex: true,
  },
  authSignup: {
    path: '/auth/signup',
    title: 'Sign Up | FIDE Prep',
    description: 'Create a free FIDE Prep account to start your preparation for the Swiss FIDE exam.',
    noindex: true,
  },
  authVerify: {
    path: '/auth/verify',
    title: 'Verify Email | FIDE Prep',
    description: 'Verify your email address to complete your FIDE Prep registration.',
    noindex: true,
  },
  terms: {
    path: '/terms_and_conditions',
    title: 'Terms and Conditions | FIDE Prep (CH)',
    description: 'Terms and Conditions for FIDE Test Preparation (Switzerland) | A1,A2,B1',
    noindex: true,
  },
  privacy: {
    path: '/privacy_policy',
    title: 'Privacy Policy | FIDE Prep (CH)',
    description: 'Privacy Policy for FIDE Test Preparation (Switzerland) | A1,A2,B1',
    noindex: true,
  },
  contact: {
    path: '/contact-us',
    title: 'Contact FIDE Prep | Swiss FIDE Test Preparation',
    description:
      'Contact FIDE Prep for questions, feedback, or correction requests. Email, phone, and postal address in Vevey, Switzerland.',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact FIDE Prep',
      url: 'https://fideprep.ch/contact-us',
      mainEntity: {
        '@type': 'Organization',
        '@id': 'https://fideprep.ch/#organization',
        name: 'FIDE Prep',
        url: 'https://fideprep.ch/',
        email: 'info@fideprep.ch',
        telephone: '+41 21 553 62 18',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'info@fideprep.ch',
          telephone: '+41 21 553 62 18',
          contactType: 'customer support',
          areaServed: 'CH',
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Avenue de Gilamont 17',
          postalCode: '1800',
          addressLocality: 'Vevey',
          addressRegion: 'Vaud',
          addressCountry: 'CH',
        },
      },
    },
  },
  notFound: {
    path: '/404.html',
    title: '404 | Page Not Found | FIDE Prep (CH)',
    description: 'The page you were looking for could not be found on FIDE Prep.',
    noindex: true,
  },
  examTipsFr: {
    path: buildExamTipPath('fr', FREE_TIP_ID),
    title: 'FIDE French Exam Tips | Strategies to Pass',
    description:
      'French FIDE exam tips for A1–B1: practical speaking, writing and grammar strategies to help you pass with confidence.',
    alternates: buildAlternates({
      fr: buildExamTipPath('fr', FREE_TIP_ID),
      de: buildExamTipPath('de', FREE_TIP_ID),
    }),
  },
  examTipsDe: {
    path: buildExamTipPath('de', FREE_TIP_ID),
    title: 'FIDE German Exam Tips | Strategies to Pass',
    description:
      'German FIDE exam tips for A1–B1: practical speaking, writing and grammar strategies to help you pass with confidence.',
    alternates: buildAlternates({
      fr: buildExamTipPath('fr', FREE_TIP_ID),
      de: buildExamTipPath('de', FREE_TIP_ID),
    }),
  },
  pricing: {
    path: '/pricing',
    title: 'Pricing | FIDE Exam Prep Plans | FIDE Prep',
    description:
      'FIDE test preparation plans: start free, then unlock mock exams, topics, vocabulary and speaking practice with Full access.',
  },
};

export const buildExamOverviewMeta = (lang = 'fr') => {
  const normalizedLang = normalizeLanguage(lang);
  const isGerman = normalizedLang === 'de';

  return {
    path: buildExamOverviewPath(normalizedLang),
    title: isGerman
      ? 'FIDE Exam / Prüfung (German) | Oral and Written Format A1-B1'
      : 'fide Test: Interactive Exam Guide with Free Sample Questions',
    description: isGerman
      ? 'Understand the German FIDE exam (Prüfung) in Switzerland. See the oral and written format, task types, timing and scoring for A1, A2 and B1.'
      : 'See the French FIDE exam format in Switzerland: oral and written tasks, timing and scoring. Try free sample questions and a mock oral exam for A1-B1.',
    alternates: buildAlternates({
      fr: buildExamOverviewPath('fr'),
      de: buildExamOverviewPath('de'),
    }),
  };
};

export const buildMockExamsMeta = (lang = 'fr', examType = 'oral') => {
  const normalizedLang = normalizeLanguage(lang);
  const isGerman = normalizedLang === 'de';
  const isListening = examType === 'listening';

  return {
    path: buildMockExamsPath(normalizedLang, examType),
    title: isListening
      ? isGerman
        ? 'FIDE Modelltest Verstehen | Official-Style A1-B1 Exams'
        : 'FIDE Sample Test Comprendre | Official-Style A1-B1 Exams'
      : isGerman
        ? 'FIDE Modelltest A1-B1 | Try a Free German Oral Mock Exam'
        : 'FIDE Sample Test A1-B1 | Try a Free French Oral Mock Exam',
    description: isListening
      ? isGerman
        ? 'Practice with realistic FIDE Modelltest examples, listening model test questions, and sample answers for A1, A2 and B1.'
        : 'Practice with realistic FIDE test examples, listening sample test questions, and sample answers for A1, A2 and B1.'
      : isGerman
        ? 'Practice with realistic FIDE Modelltest examples, oral model test questions, and sample answers for A1, A2 and B1.'
        : 'Practice with realistic FIDE test examples, oral sample test questions, and sample answers for A1, A2 and B1.',
    alternates: buildAlternates({
      fr: buildMockExamsPath('fr', examType),
      de: buildMockExamsPath('de', examType),
    }),
  };
};

export const buildExamTipMeta = ({ lang = 'fr', tip, noindex = false }) => {
  const normalizedLang = normalizeLanguage(lang);
  const contentKey = `en-${normalizedLang}`;
  const tipContent = tip?.content?.[contentKey] || tip?.content?.['en-fr'];
  const languageLabel = normalizedLang === 'de' ? 'German' : 'French';
  const titleText = tip?.title?.en || tip?.lockedTitle?.en || 'FIDE Exam Tip';

  return {
    path: buildExamTipPath(normalizedLang, tip.id),
    title:
      tip.id === FREE_TIP_ID
        ? PAGE_META[normalizedLang === 'de' ? 'examTipsDe' : 'examTipsFr'].title
        : `${titleText} (${languageLabel})`,
    // Most tips share one English `tldr` across `en-fr` and `en-de`, so the language
    // prefix is what keeps the French and German tip URLs from shipping identical
    // meta descriptions (flagged by Bing).
    description: tipContent?.tldr
      ? capMetaDescription(`${languageLabel} FIDE tip: ${tipContent.tldr.replace(/\[\[|\]\]/g, '')}`)
      : capMetaDescription(PAGE_META[normalizedLang === 'de' ? 'examTipsDe' : 'examTipsFr'].description),
    noindex,
    alternates: buildAlternates({
      fr: buildExamTipPath('fr', tip.id),
      de: buildExamTipPath('de', tip.id),
    }),
  };
};

export const buildExampleVideoMeta = ({ videoId, video, noindex = false, position }) => {
  const topics = Array.isArray(video?.topics) && video.topics.length > 0 ? video.topics : [];
  const levels = Array.isArray(video?.levels) && video.levels.length > 0 ? video.levels : [];
  const topicLabel = formatTopicList(topics);
  const levelLabel = formatLevelList(levels);
  const sampleLabel = position ? `Sample ${position}` : `Sample ${String(videoId || '').slice(0, 6) || 'Video'}`;
  const title = `${sampleLabel}: ${topicLabel} FIDE Example Video | ${levelLabel} | FIDE Prep`;
  const description = capMetaDescription(
    `French FIDE test example — ${sampleLabel}: ${topicLabel}. Watch this ${levelLabel} sample video with a transcript to practice listening and speaking.`,
  );
  const path = buildExampleVideoPath(videoId);
  const canonicalUrl = buildCanonicalUrl(path);

  return {
    path,
    title,
    description,
    noindex,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: `${sampleLabel}: ${topicLabel} FIDE Example Video`,
      description,
      thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      embedUrl: `https://www.youtube.com/embed/${videoId}`,
      url: canonicalUrl,
      isAccessibleForFree: true,
      ...(video?.uploadDate ? { uploadDate: video.uploadDate } : {}),
    },
  };
};

export const buildTopicsVideoMeta = ({
  lang = 'fr',
  scenario,
  topicTitle,
  videoGuid,
  uploadDate,
  noindex = false,
  pathOverride,
}) => {
  const normalizedLang = normalizeLanguage(lang);
  const scenarioTitle = getLocalizedText(scenario?.title, normalizedLang) || 'FIDE Topic Video';
  const resolvedTopicTitle = getLocalizedText(topicTitle, normalizedLang);
  const languageLabel = normalizedLang === 'de' ? 'German' : 'French';
  const freeAccessLabel = scenario?.free ? 'free' : 'premium';
  const description = capMetaDescription(
    `${languageLabel} FIDE test example: ${scenarioTitle}. Watch this ${freeAccessLabel} dialogue video to practice listening, speaking and oral-exam responses.`,
  );
  const path = pathOverride || buildTopicsVideoPath(normalizedLang, scenario.id);
  const canonicalUrl = buildCanonicalUrl(path);
  const resolvedUploadDate = uploadDate || scenario?.uploadDate;

  return {
    path,
    title: `${scenarioTitle} | ${resolvedTopicTitle ? `${resolvedTopicTitle} ` : ''}FIDE Dialogue Video (${languageLabel}) | FIDE Prep`,
    description,
    noindex,
    structuredData:
      videoGuid && scenario?.free && resolvedUploadDate
        ? {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: scenarioTitle,
            description,
            thumbnailUrl: `${TOPIC_VIDEO_CDN_BASE}/${videoGuid}/thumbnail.jpg`,
            embedUrl: `${TOPIC_VIDEO_PLAYER_BASE}/${videoGuid}`,
            url: canonicalUrl,
            isAccessibleForFree: !!scenario?.free,
            uploadDate: resolvedUploadDate,
          }
        : undefined,
  };
};

export const buildPublicDialogueVideoMeta = ({ lang, scenarioId, pathOverride }) => {
  const normalizedLang = normalizeLanguage(lang);
  const video = getPublicDialogueVideoFallback(scenarioId);

  if (!video) {
    return null;
  }

  return buildTopicsVideoMeta({
    lang: normalizedLang,
    scenario: {
      id: Number(scenarioId),
      free: true,
      title: video.title,
      uploadDate: video.uploadDate,
    },
    topicTitle: video.topic,
    videoGuid: video.guid[normalizedLang],
    uploadDate: video.uploadDate,
    pathOverride,
  });
};

export const buildPracticeMapMeta = (lang = 'fr') => {
  const normalizedLang = normalizeLanguage(lang);
  const isGerman = normalizedLang === 'de';

  return {
    path: buildPracticePath(normalizedLang),
    title: isGerman
      ? 'FIDE Speaking Practice (German) | A1-B1 Guided Path'
      : 'FIDE Speaking Practice (French) | A1-B1 Guided Path',
    description: isGerman
      ? 'Practice the German FIDE oral exam step by step: introduction, image description, telephone dialogue and discussion, with sample answers and audio.'
      : 'Practice the French FIDE oral exam step by step: introduction, image description, telephone dialogue and discussion, with sample answers and audio.',
    alternates: buildAlternates({ fr: buildPracticePath('fr'), de: buildPracticePath('de') }),
  };
};

/**
 * The map's topic-catalog tab is `/{lang}/fide-test-topics` — the URL the retired
 * standalone topics browser used, so it keeps that page's meta rather than a new
 * variant competing with it.
 */
export const buildTopicsMapMeta = (lang = 'fr') =>
  normalizeLanguage(lang) === 'de' ? PAGE_META.topicsDe : PAGE_META.topicsFr;

export const buildPracticeSectionMeta = (lang = 'fr', level = 'a2', section = 'intro', label = '') => {
  const normalizedLang = normalizeLanguage(lang);
  const isGerman = normalizedLang === 'de';
  const language = isGerman ? 'German' : 'French';
  const levelLabel = String(level).toUpperCase();

  return {
    path: buildPracticeSectionPath(normalizedLang, level, section),
    title: `${label} (${language}) | FIDE ${levelLabel} Speaking Practice`,
    description: isGerman
      ? `Practice the ${label} task of the German FIDE ${levelLabel} oral exam with sample answers, audio and vocabulary.`
      : `Practice the ${label} task of the French FIDE ${levelLabel} oral exam with sample answers, audio and vocabulary.`,
    alternates: buildAlternates({
      fr: buildPracticeSectionPath('fr', level, section),
      de: buildPracticeSectionPath('de', level, section),
    }),
  };
};

/**
 * For genuinely premium-locked content only — the dialogue and discussion
 * scenarios. It is NOT for the vocabulary or flashcard pages: those are free in
 * full to guests, and marking them paywalled suppresses exactly the pages this
 * site wants indexed. Currently unused; kept for the locked scenario pages.
 */
export const PAYWALL_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  isAccessibleForFree: false,
  hasPart: {
    '@type': 'WebPageElement',
    isAccessibleForFree: false,
    cssSelector: '.paywall',
  },
};
